/**
 * rockCapabilityWarnings.ts
 *
 * Phase 5, item 5.4 (REQ-04 "nearly one-to-one"):
 *   Validates the one-machine-per-rock-test expectation and surfaces soft
 *   warnings (never a hard block) when a rock TaskType has >1 capable machine.
 *
 * The two documented exceptions (UCS / UCS with Modulus) are allowed to map
 * to both RK-LF01 and RK-TX01 — they must NOT trigger the generic warning.
 *
 * Usage:
 *   import { computeRockCapabilityWarnings } from './rockCapabilityWarnings';
 *   const warnings = computeRockCapabilityWarnings(machines);
 */

import type { MachineRow } from '../services/scheduleMachines';

/**
 * The two rock task types that legitimately run on MORE than one machine.
 * These are the catalog names (from Appendix B crosswalk):
 *   - "UCS" XLSX name → "Uniaxial Compressive Strength" in the catalog.
 *   - "UCS with Modulus" is exact.
 *
 * Comparison is case-insensitive to be robust against trim/capitalisation drift.
 */
export const DUAL_MACHINE_ROCK_TESTS: ReadonlySet<string> = new Set([
  'uniaxial compressive strength',
  'ucs with modulus',
]);

/** Result of the capability count analysis for one rock TaskType. */
export interface RockCapabilityWarning {
  /** TaskType primary key. */
  taskTypeKey: string;
  /** Human-readable test name (catalog name). */
  taskTypeName: string;
  /** How many active machines are mapped to this test. */
  machineCount: number;
  /** True when this test is the known-exception pair (UCS / UCS with Modulus). */
  isKnownException: boolean;
}

/**
 * Derives per-rock-task-type machine counts from the already-loaded machine list.
 *
 * The machine list (from GET /api/machines) already joins TaskTypes with Category,
 * so we can compute counts without a second API call.
 *
 * Returns ONLY task types that have >1 machine mapped.
 * Caller can inspect `isKnownException` to decide how to present each item.
 */
export function computeRockCapabilityWarnings(
  machines: MachineRow[],
): RockCapabilityWarning[] {
  // Accumulate count per task type across all machines (rock only)
  const counts = new Map<string, { name: string; count: number }>();

  for (const machine of machines) {
    for (const tt of machine.TaskTypes) {
      if (tt.Category !== 'Rock Testing') continue;
      const existing = counts.get(tt.TaskTypeKey);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(tt.TaskTypeKey, { name: tt.Name, count: 1 });
      }
    }
  }

  // Return only the items with >1 machine mapped
  const warnings: RockCapabilityWarning[] = [];
  for (const [key, { name, count }] of counts) {
    if (count <= 1) continue;
    const isKnownException = DUAL_MACHINE_ROCK_TESTS.has(name.toLowerCase().trim());
    warnings.push({ taskTypeKey: key, taskTypeName: name, machineCount: count, isKnownException });
  }

  // Sort: unexpected warnings first, then known exceptions
  warnings.sort((a, b) => {
    if (a.isKnownException !== b.isKnownException) {
      return a.isKnownException ? 1 : -1;
    }
    return a.taskTypeName.localeCompare(b.taskTypeName);
  });

  return warnings;
}

/**
 * Returns only the warnings that are NOT known exceptions — i.e., the cases
 * that need admin attention (unexpected multi-machine rock tests).
 */
export function unexpectedRockWarnings(
  machines: MachineRow[],
): RockCapabilityWarning[] {
  return computeRockCapabilityWarnings(machines).filter((w) => !w.isKnownException);
}
